import * as React from 'react';
import up = require('updeep');
import { compose, Dispatch, bindActionCreators } from 'redux';
import { isEmpty, trim } from 'lodash';
import { Alert, FormGroup, Input, Button, FormFeedback, Label } from 'reactstrap';
import { connect } from 'react-redux';
import { State } from '../../redux/state';
import actions from '../../redux/actions';
import { LProps, withLocalization } from '../../utils/wrappers';
import { ForgotPasswordErrors, makeForgotPasswordErrors, forgotPasswordVEMessages } from '../../utils/forms';
import { ValidationErrors, removeVEForField } from '../../userApi';
import RouteLink from '../../shared/RouteLink';
import { Routes } from '../../utils/routerHelper';

interface Props {
  validationErrors: ValidationErrors;
  postPasswordResetRequest: (form: { email: string }) => Promise<boolean>;
  patch: (update: Partial<State>) => void;
}

interface ForgotPasswordState {
  errors: Partial<ForgotPasswordErrors>;
  form: { email: string };
  dirty: { [key: string]: boolean };
  submitted: boolean;
  sentTo: string;
  sending: boolean;
}

const initialState: ForgotPasswordState = {
  errors: {},
  form: { email: '' },
  dirty: {},
  submitted: false,
  sentTo: '',
  sending: false
};

class ForgotPassword extends React.Component<LProps<Props>, ForgotPasswordState> {
  public state = initialState;
  private successRef: HTMLDivElement | null = null;

  public componentDidUpdate(_prevProps: LProps<Props>, prevState: ForgotPasswordState) {
    if (!prevState.sentTo && this.state.sentTo && this.successRef) {
      this.successRef.focus();
    }
  }

  private update(update: Partial<ForgotPasswordState>, validate: boolean = false) {
    const { dirty, submitted, form } = this.state;
    const { validationErrors, t } = this.props;
    if (validate) {
      const errors = makeForgotPasswordErrors({ dirty, submitted, ...form }, validationErrors, t);
      if (!isEmpty(errors)) {
        update.errors = errors;
      }
    }
    this.setState(update as ForgotPasswordState);
  }

  private markDirty(field: string) {
    const { submitted, form } = this.state;
    const dirty = { ...this.state.dirty, [field]: true };
    const { validationErrors, t, patch } = this.props;
    let ve = validationErrors;
    if (forgotPasswordVEMessages[field]) {
      ve = removeVEForField(field, validationErrors, forgotPasswordVEMessages);
      patch({ forgotPasswordVE: ve });
    }

    const update: Partial<ForgotPasswordState> = { dirty: up({ [field]: true }, this.state.dirty) };
    const errors = makeForgotPasswordErrors({ dirty, submitted, ...form }, ve, t);
    update.errors = errors;
    this.setState({ errors, dirty });
  }

  private async submit() {
    const { postPasswordResetRequest, validationErrors, t } = this.props;
    const submitted = true;
    const { form, dirty } = this.state;

    const errors = makeForgotPasswordErrors({ dirty, submitted, ...form }, validationErrors, t);
    this.setState({ submitted, errors });
    if (!isEmpty(errors)) {
      return;
    }
    this.setState({ sending: true });
    const ok = await postPasswordResetRequest(form);
    if (ok) {
      this.setState({ sending: false, sentTo: form.email });
    } else {
      this.setState({ sending: false });
    }
  }

  public render() {
    const { form, errors, sentTo, sending } = this.state;
    const { t } = this.props;

    if (sentTo) {
      return (
        <div id="forgot_password" className="home__form">
          <div
            ref={(el) => { this.successRef = el; }}
            tabIndex={-1}
            role="status"
            aria-live="polite"
          >
            <Alert color="success">
              <strong>{t('Check your email')}</strong>
              <p style={{ margin: '8px 0 0' }}>
                {t('If an account exists for {{email}}, we sent a password reset link. Open that email and follow the link within one hour.', { email: sentTo })}
              </p>
              <p style={{ margin: '8px 0 0' }}>
                {t('Didn’t get it? Check spam/junk, then try again.')}
              </p>
            </Alert>
          </div>
          <Button
            block={true}
            color="secondary"
            className="mt-2"
            onClick={() => this.setState({ sentTo: '', submitted: false })}
          >{t('Send another link')}</Button>
          <div style={{ margin: '12px 0 0' }}>
            <RouteLink route={Routes.Login}>{t('Back to log in')}</RouteLink>
          </div>
        </div>
      );
    }

    return (
      <div id="forgot_password" className="home__form">
        <FormGroup
          id="forgot_password__email"
        >
          <Label for="forgot_password__email_input">{t('Email*')}</Label>
          <Input
            id="forgot_password__email_input"
            type="email"
            autoComplete="email"
            aria-required={true}
            aria-invalid={Boolean(errors.email)}
            invalid={Boolean(errors.email)}
            value={form.email}
            onBlur={() => this.markDirty('email')}
            onChange={(e) => this.update({ form: { ...form, email: trim(e.target.value || '').toLowerCase() } })}
          />
          {errors.email && <FormFeedback>{errors.email}</FormFeedback>}
        </FormGroup>
        <Button
          block={true}
          color="primary"
          disabled={sending}
          aria-busy={sending}
          onClick={() => this.submit()}
        >{sending ? t('Sending…') : t('Send link')}</Button>
      </div>
    );
  }
}

const mapStateToProps = (state: State, _ownProps: Props): Partial<Props> => ({
  validationErrors: state.forgotPasswordVE
});
const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
  postPasswordResetRequest: actions.postPasswordResetRequest,
  patch: actions.patch
}, dispatch);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLocalization('forgotPassword')
)(ForgotPassword) as React.ComponentClass;
